/**
 * Channels — the COGS view, one card per channel, on the units each one sells.
 *
 * A phone channel is bought per CONNECTED call and per carrier minute; a
 * screen channel per session and per engaged minute; messaging per message.
 * Every figure names its denominator, every rate is the same all-in rate
 * split by what it is made of, and analysis that ran afterwards is charged
 * to the conversation it analysed (once). The rankings and mixes from the
 * old Comparison tab sit underneath — they answer "which one", this answers
 * "how much, per what".
 */
import React from 'react';
import { AdminAPI, UsageChannelRow, UsageFilterState, UsageReportSummary } from '../../lib/api';
import { ComparisonTab } from './ComparisonTab';
import {
  CHANNEL_LABEL, Card, Chip, ExportCsv, MODALITY_COLOR, Money, Notice, downloadCsv, int, minutes, pct, useCurrency,
} from './shared';

const UNIT_PLURAL: Record<string, string> = { call: 'connected calls', session: 'sessions', message: 'messages' };

export const ChannelsTab: React.FC<{
  api: AdminAPI;
  filters: UsageFilterState;
  summary: UsageReportSummary | null;
  nonce: number;
  onDrill: (patch: Record<string, string | undefined>) => void;
}> = ({ api, filters, summary, nonce, onDrill }) => {
  const rows = (summary?.by_channel ?? []).filter((c) => c.channel !== 'unknown');
  const conversation = rows.filter((c) => c.channel !== 'system');
  const system = rows.find((c) => c.channel === 'system');
  return (
    <div className="space-y-4">
      {!rows.length && <Notice>No spend in this window. Widen the range or clear a filter.</Notice>}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {conversation.map((c) => (
          <ChannelCogs key={c.channel} c={c} summary={summary} filters={filters} onDrill={onDrill} />
        ))}
        {system && <SystemCogs c={system} summary={summary} filters={filters} />}
      </div>
      <ComparisonTab api={api} filters={filters} summary={summary} nonce={nonce} onDrill={onDrill} />
    </div>
  );
};

/** One labelled figure. `hint` is the denominator, spelled out. */
const Kv: React.FC<{ label: string; value: React.ReactNode; hint?: string; sub?: React.ReactNode; tone?: 'bad' | 'good' }> = ({ label, value, hint, sub, tone }) => (
  <div className="min-w-0" title={hint}>
    <dt className="truncate text-[10px] uppercase tracking-wide text-gray-400">{label}</dt>
    <dd className={`text-sm font-medium tabular-nums ${tone === 'bad' ? 'text-rose-600' : tone === 'good' ? 'text-emerald-700' : 'text-gray-900'}`}>{value}</dd>
    {sub && <dd className="text-[10px] text-gray-500">{sub}</dd>}
  </div>
);

const Group: React.FC<{ title: string; children: React.ReactNode; cols?: number }> = ({ title, children, cols = 3 }) => (
  <div>
    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</div>
    <dl className={`grid gap-x-4 gap-y-2 ${cols === 4 ? 'grid-cols-4' : cols === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>{children}</dl>
  </div>
);

/** The all-in rate, as a bar of what it is made of. */
const RateBar: React.FC<{ c: UsageChannelRow }> = ({ c }) => {
  const parts: [string, number | null][] = [
    ['llm', c.llm_per_minute], ['tts', c.tts_per_minute], ['stt', c.stt_per_minute], ['telephony', c.telephony_per_minute],
  ];
  const total = parts.reduce((a, [, v]) => a + (v ?? 0), 0);
  if (!total) return null;
  return (
    <div className="flex h-2.5 w-full overflow-hidden rounded">
      {parts.map(([k, v]) => (v ?? 0) > 0 && (
        <div key={k} style={{ width: `${((v ?? 0) / total) * 100}%`, background: MODALITY_COLOR[k as keyof typeof MODALITY_COLOR] }} />
      ))}
    </div>
  );
};

const windowDays = (f: UsageFilterState) =>
  Math.max(1, Math.round((new Date(f.to).getTime() - new Date(f.from).getTime()) / 86400000) + 1);

/** Flatten one channel's KPIs to metric/value rows for CSV. */
const kpiRows = (c: UsageChannelRow, funnel: UsageReportSummary['phone_funnel'] | undefined, ccy: string, toDisplay: (n: number) => number) => {
  const m = (v: number | null | undefined) => (v == null ? '' : toDisplay(v));
  const rows: Record<string, unknown>[] = [
    { metric: 'channel', value: c.channel },
    { metric: 'unit', value: c.unit_label },
    { metric: 'units', value: c.units },
    { metric: 'sessions', value: c.sessions },
    { metric: 'rated_minutes', value: c.rated_minutes },
    { metric: `cost_total_${ccy}`, value: m(c.session_cost_usd) },
    { metric: `cost_per_${c.unit_label}_${ccy}`, value: m(c.cost_per_unit) },
    { metric: `cost_per_minute_${ccy}`, value: m(c.cost_per_minute) },
    { metric: `llm_per_minute_${ccy}`, value: m(c.llm_per_minute) },
    { metric: `tts_per_minute_${ccy}`, value: m(c.tts_per_minute) },
    { metric: `stt_per_minute_${ccy}`, value: m(c.stt_per_minute) },
    { metric: `carrier_per_minute_${ccy}`, value: m(c.telephony_per_minute) },
    { metric: 'avg_minutes_per_unit', value: c.avg_minutes_per_unit ?? '' },
    { metric: 'turns', value: c.turns },
    { metric: 'turns_per_unit', value: c.turns_per_unit ?? '' },
    { metric: `cost_per_turn_${ccy}`, value: m(c.cost_per_turn) },
    { metric: 'prompt_tokens_per_turn', value: c.tokens_per_turn ?? '' },
    { metric: 'tts_chars_per_minute', value: c.tts_chars_per_minute ?? '' },
    { metric: `overhead_total_${ccy}`, value: m(c.overhead_usd) },
    { metric: `overhead_per_unit_${ccy}`, value: m(c.overhead_per_unit) },
    { metric: `revenue_total_${ccy}`, value: m(c.revenue_usd) },
    { metric: `revenue_per_unit_${ccy}`, value: m(c.revenue_per_unit) },
    { metric: `margin_per_unit_${ccy}`, value: m(c.margin_per_unit) },
    { metric: 'margin_pct', value: c.margin_pct ?? '' },
    { metric: 'credits_billable', value: c.credits_billable },
  ];
  if (c.channel === 'phone') {
    rows.push(
      { metric: 'calls_placed', value: funnel?.placed ?? '' },
      { metric: 'calls_connected', value: funnel?.connected ?? c.connected_calls },
      { metric: 'handoffs_to_screen', value: c.handoffs },
      { metric: 'handoff_rate', value: c.handoff_rate ?? '' },
      { metric: `unconnected_attempts_cost_${ccy}`, value: m(funnel?.unconnected_cost_usd ?? null) },
    );
  }
  return rows;
};

const ChannelCogs: React.FC<{
  c: UsageChannelRow;
  summary: UsageReportSummary | null;
  filters: UsageFilterState;
  onDrill: (patch: Record<string, string | undefined>) => void;
}> = ({ c, summary, filters, onDrill }) => {
  const { fmt, ccy, toDisplay } = useCurrency();
  const pulse = summary?.pulses;
  const funnel = c.channel === 'phone' && !filters.channel ? summary?.phone_funnel ?? undefined : undefined;
  const unit = c.unit_label;
  const days = windowDays(filters);
  const perDay = c.units / days;
  const connectRate = funnel && funnel.placed ? funnel.connected / funnel.placed : null;

  return (
    <Card
      title={<span className="flex items-center gap-2"><Chip kind="channel" value={c.channel} /> {CHANNEL_LABEL[c.channel] ?? c.channel} · COGS per {unit}</span>}
      subtitle={
        c.channel === 'phone' ? 'Bought per connected call and per carrier talk-minute. Unanswered attempts are in the cost of a call, not in any rate.'
        : c.channel === 'ui' ? 'Bought per session and per engaged minute (the sum of turn wall-clocks, not elapsed time).'
        : c.channel === 'messaging' ? 'Bought per message; a template opens a 24-hour conversation.'
        : undefined
      }
      right={
        <div className="flex items-center gap-2">
          <button onClick={() => onDrill({ channel: c.channel })} className="text-[11px] text-blue-600 hover:underline">Filter</button>
          <ExportCsv onClick={() => downloadCsv(`cogs_${c.channel}_${filters.from}_${filters.to}`, [
            { key: 'metric', label: 'metric' }, { key: 'value', label: 'value' },
          ], kpiRows(c, funnel, ccy, toDisplay))} />
        </div>
      }
    >
      <div className="space-y-4">
        <Group title="Volume" cols={4}>
          <Kv label={UNIT_PLURAL[unit] ?? unit} value={int(c.units)} sub={`${perDay.toFixed(1)} / day over ${days} days`} />
          {c.channel === 'phone' && funnel && (
            <Kv label="Placed → connected" value={`${int(funnel.placed)} → ${int(funnel.connected)}`}
                sub={connectRate != null ? `${pct(connectRate * 100, 0)} connect rate` : undefined}
                hint="From the call log: every attempt, whether or not it produced a ledger row." />
          )}
          {c.channel === 'phone' && (
            <Kv label="Handoffs to screen" value={int(c.handoffs)}
                sub={c.handoff_rate != null ? `${pct(c.handoff_rate * 100, 0)} of connected` : undefined}
                hint="Calls that sent a link and continued on the screen (disposition handoff_ui)." />
          )}
          {c.channel !== 'phone' && <Kv label="Sessions" value={int(c.sessions)} />}
          <Kv label="Turns" value={int(c.turns)} sub={c.turns_per_unit != null ? `${c.turns_per_unit} per ${unit}` : undefined} hint="LLM turns — the unit the model bill scales with." />
        </Group>

        <Group title="Time" cols={3}>
          <Kv label={c.channel === 'phone' ? 'Talk minutes (carrier)' : 'Engaged minutes'} value={minutes(c.rated_minutes)}
              hint={c.channel === 'phone' ? 'Carrier-metered talk time on connected calls.' : 'Sum of per-turn wall clocks.'} />
          <Kv label={`Avg per ${unit}`} value={c.avg_minutes_per_unit != null ? `${c.avg_minutes_per_unit} min` : '—'} />
          {c.channel === 'phone' && funnel && (
            <Kv label="Billed minutes" value={minutes(funnel.billed_seconds / 60)} hint="Carrier billing increments (60s minimum), which is what the carrier invoices." />
          )}
        </Group>

        <Group title={`COGS per ${unit}`} cols={3}>
          <Kv label={`All-in / ${unit}`} value={<span className="text-base font-semibold"><Money value={c.allin_per_unit} inr={c.allin_per_unit_inr} /></span>}
              hint={`Everything one ${unit} costs: LLM, speech, carrier, and the analysis it caused. ÷ ${UNIT_PLURAL[unit]}.`} />
          <Kv label={`Conversation / ${unit}`} value={<Money value={c.cost_per_unit} inr={c.cost_per_unit_inr} />}
              hint="LLM + speech + carrier only, including unanswered attempts." />
          <Kv label={`Analysis / ${unit}`} value={<Money value={c.overhead_per_unit} inr={c.overhead_per_unit_inr} />}
              hint="Post-conversation analysis and compliance, charged once per unit. Per unit, never per minute: the summariser does not run longer on a longer call." />
        </Group>

        <div>
          <div className="mb-1.5 flex items-baseline justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500" title="LLM + speech + carrier over this channel's clock. Analysis is per unit and is not in here.">Rate per minute · talk time</span>
            <span className="text-sm font-semibold tabular-nums text-gray-900"><Money value={c.cost_per_minute} inr={c.cost_per_minute_inr} bare />{c.cost_per_minute != null && <span className="text-[10px] font-normal text-gray-400"> / min</span>}</span>
          </div>
          <RateBar c={c} />
          <div className="mt-1.5 grid grid-cols-4 gap-x-3 text-[11px] tabular-nums">
            {([['llm', 'LLM', c.llm_per_minute], ['tts', 'Speech out', c.tts_per_minute], ['stt', 'Speech in', c.stt_per_minute], ['telephony', 'Carrier', c.telephony_per_minute]] as [string, string, number | null][]).map(([k, label, v]) => (
              <div key={k} className="flex items-center gap-1 text-gray-600">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: MODALITY_COLOR[k as keyof typeof MODALITY_COLOR] }} />
                {label} <span className="font-medium text-gray-900">{fmt(v, undefined, 4)}</span>
              </div>
            ))}
          </div>
          {c.cost_per_minute == null && <div className="mt-1 text-[11px] text-gray-400">No rate: under half a minute of measured time in this window.</div>}
        </div>

        <Group title="Efficiency" cols={3}>
          <Kv label="Cost / turn" value={<Money value={c.cost_per_turn} bare />} hint="Session cost ÷ LLM turns." />
          <Kv label="Prompt tokens / turn" value={c.tokens_per_turn != null ? int(c.tokens_per_turn) : '—'}
              hint="Average input tokens per LLM call: state instructions + knowledge + history. The LLM lever."
              tone={c.tokens_per_turn != null && c.tokens_per_turn > 12000 ? 'bad' : undefined} />
          <Kv label={c.channel === 'ui' ? 'Voice share' : 'TTS chars / min'}
              value={c.channel === 'ui' ? (c.voice_share_pct != null ? pct(c.voice_share_pct, 0) : '—') : (c.tts_chars_per_minute != null ? int(c.tts_chars_per_minute) : '—')}
              hint={c.channel === 'ui' ? 'Share of spend that is speech in + out.' : 'Characters synthesised per talk-minute. The TTS lever.'} />
          <Kv label="STT per request" value={<Money value={c.stt_per_request} bare />}
              sub={c.stt_seconds_per_request != null ? `${c.stt_seconds_per_request}s audio avg${pulse?.stt?.minimum_seconds ? ` · billed ≥${pulse.stt.minimum_seconds}s` : ''}` : undefined}
              hint="What one transcription request costs, at the provider's billed seconds (minimum per request applies)." />
        </Group>

        <Group title="Price side" cols={3}>
          <Kv label={`Revenue / ${unit}`} value={<Money value={c.revenue_per_unit} bare />} sub={`${int(c.credits_billable)} cr billed`} hint="From the rate cards on these sessions." />
          <Kv label={`Margin / ${unit}`} value={<Money value={c.margin_per_unit} bare />} tone={c.margin_per_unit != null && c.margin_per_unit < 0 ? 'bad' : c.margin_per_unit != null ? 'good' : undefined} />
          <Kv label="Margin" value={c.margin_pct == null ? '—' : pct(c.margin_pct)} tone={c.margin_pct != null && c.margin_pct < 0 ? 'bad' : undefined} />
        </Group>
        {c.unpriced_sessions > 0 && (
          <Notice tone="warn">{int(c.unpriced_sessions)} sessions resolved to no rate card and bill nothing.</Notice>
        )}
      </div>
    </Card>
  );
};

const SystemCogs: React.FC<{ c: UsageChannelRow; summary: UsageReportSummary | null; filters: UsageFilterState }> = ({ c, summary, filters }) => {
  const { ccy, toDisplay } = useCurrency();
  const conversations = (summary?.by_channel ?? []).filter((x) => x.channel !== 'system' && x.channel !== 'unknown').reduce((a, x) => a + x.sessions, 0);
  const attributed = summary?.back_office?.attributed_usd ?? (summary?.by_channel ?? []).reduce((a, x) => a + (x.overhead_usd || 0), 0);
  const unattributed = summary?.back_office?.unattributed_usd ?? Math.max(0, c.cost_usd - attributed);
  const byFeature = summary?.back_office?.by_feature ?? [];
  return (
    <Card
      title={<span className="flex items-center gap-2"><Chip kind="channel" value="system" /> Back-office · overhead</span>}
      subtitle="Analysis, compliance, ingest and agent building. Real spend, never a session; the analysis part is charged to the conversations it ran on."
      right={<ExportCsv onClick={() => downloadCsv(`cogs_system_${filters.from}_${filters.to}`, [
        { key: 'metric', label: 'metric' }, { key: 'value', label: 'value' },
      ], [
        { metric: `total_${ccy}`, value: toDisplay(c.cost_usd) },
        { metric: `attributed_to_conversations_${ccy}`, value: toDisplay(attributed) },
        { metric: 'conversations', value: conversations },
        { metric: `per_conversation_${ccy}`, value: conversations ? toDisplay(attributed / conversations) : '' },
        { metric: 'units', value: c.non_session_units },
      ])} />}
    >
      <dl className="grid grid-cols-4 gap-x-4 gap-y-2">
        <Kv label="Total" value={<Money value={c.cost_usd} inr={c.cost_inr} />} sub={`${int(c.non_session_units)} units`} />
        <Kv label="Charged to channels" value={<Money value={attributed} bare />} hint="Analysis and compliance that ran on a conversation — in each channel's COGS per unit above." tone="good" />
        <Kv label="Per conversation" value={<Money value={conversations ? attributed / conversations : null} bare />} sub={`${int(conversations)} conversations`} />
        <Kv label="Not attributable" value={<Money value={unattributed} bare />} hint="Agent building, ingest, preset synthesis, reports: business overhead, no channel." />
      </dl>
      {byFeature.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
          {byFeature.map((f) => <span key={f.feature}>{f.feature} <span className="font-medium text-gray-700"><Money value={f.cost_usd} bare /></span></span>)}
        </div>
      )}
    </Card>
  );
};
